import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { X } from "lucide-react";
import { fontSize, fontWeight, textColor, bgColor, borderColor } from "@/theme";

interface MasterModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isLoading?: boolean;
  children: React.ReactNode;
}

export default function MasterModal({
  isOpen,
  title,
  onClose,
  onSubmit,
  isLoading = false,
  children,
}: MasterModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-opacity-80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center py-8 px-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full max-w-2xl max-h-[75vh] transform flex flex-col rounded-xl ${bgColor.white} shadow-xl transition-all border ${borderColor.gray100}`}>
                {/* Header */}
                <div className="relative px-6 py-3 flex-shrink-0 rounded-t-xl" style={{ background: 'linear-gradient(135deg, #2d3440 0%, #535c6b 100%)' }}>
                  <Dialog.Title as="h3" className={`${fontSize.lg} ${fontWeight.bold} ${textColor.white} pr-8`}>
                    {title}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={onClose}
                    className={`absolute right-4 top-4 ${textColor.white} hover:text-black hover:bg-white hover:bg-opacity-20 p-1 rounded-lg transition`}
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="px-6 py-4 overflow-y-auto flex-1">
                  {children}
                </div>

                <div className="flex gap-3 px-6 pb-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      onSubmit(e);
                    }}
                    disabled={isLoading}
                    className={`flex-1 bg-mlb-gold hover:bg-mlb-gold/80 disabled:opacity-50 text-brand-700 font-semibold py-2.5 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md`}
                  >
                    {isLoading ? "Saving..." : "Save"}
                  </button>
                  <button
                    type="button"
                    onClick={onClose}
                    className={`flex-1 ${bgColor.gray200} hover:${bgColor.gray300} ${textColor.heading} ${fontWeight.medium} py-2.5 rounded-lg transition-colors`}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
